import { API_DOMAIN } from "@monorepo/constants";
import { formatPhone } from "@monorepo/helpers";
import {
  IAuthClient,
  IChangePasswordPayload,
  IChangePasswordSuccessResponse,
  IClientInfo,
  IClientPayload,
  IGenericSuccessResponse,
  IGetChangePasswordQuestion,
  IGetChangePasswordQuestionPayload,
  IGetTokenSuccessResponse,
  IPhoneCheckSuccessResponse,
  IRegisterConfirmPayload,
  IUpdatePassword,
  IUpdatePasswordPayload,
} from "@monorepo/types";
import { createApi } from "@reduxjs/toolkit/query/react";

import { customBaseQuery } from "../../helpers/baseQuery";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: customBaseQuery<{ meta: IPhoneCheckSuccessResponse | IGenericSuccessResponse | IGetTokenSuccessResponse }>(
    {
      baseUrl: API_DOMAIN,
    }
  ),
  tagTypes: ["Client"],
  endpoints: (builder) => ({
    getToken: builder.mutation<IGetTokenSuccessResponse, { phone: string; password: string }>({
      query: (payload) => ({
        url: "/api/v1/web/get-token",
        method: "POST",
        body: {
          phone: formatPhone(payload.phone),
          web_password: payload.password,
        },
      }),
      invalidatesTags: ["Client"],
    }),

    phoneCheck: builder.mutation<IPhoneCheckSuccessResponse, string>({
      query: (phone) => ({
        url: "/api/v1/phone-check",
        method: "POST",
        body: {
          phone: formatPhone(phone),
        },
      }),
      transformResponse: (response: { meta: IPhoneCheckSuccessResponse }) => {
        return response?.meta;
      },
    }),

    register: builder.mutation<IGenericSuccessResponse, { phone: string; password: string; client_secret: string }>({
      query: (payload) => ({
        url: "/api/v1/web/register",
        method: "POST",
        body: {
          phone: formatPhone(payload.phone),
          web_password: payload.password,
          client_secret: payload.client_secret,
        },
      }),
      transformResponse: (response: { meta: IGenericSuccessResponse }) => {
        return response?.meta;
      },
    }),

    verifySms: builder.mutation<IGenericSuccessResponse, { phone: string; client_secret: string }>({
      query: (payload) => ({
        url: "/api/v1/web/check-verify-sms",
        method: "POST",
        body: {
          phone: formatPhone(payload.phone),
          client_secret: payload.client_secret,
        },
      }),
      transformResponse: (response: { meta: IGenericSuccessResponse }) => {
        return response?.meta;
      },
    }),

    sendVerifySms: builder.mutation<IGenericSuccessResponse, { phone: string; retry: boolean }>({
      query: (payload) => ({
        url: "/api/v1/web/send-verify-sms",
        method: "POST",
        body: {
          phone: formatPhone(payload.phone),
          retry: payload.retry,
        },
      }),
      transformResponse: (response: { meta: IGenericSuccessResponse }) => {
        return response?.meta;
      },
    }),

    registerConfirm: builder.mutation<IGetTokenSuccessResponse, IRegisterConfirmPayload>({
      query: (payload) => ({
        url: " /api/v1/web/register-confirm",
        method: "POST",
        body: {
          ...payload,
          phone: formatPhone(payload.phone),
        },
      }),
      transformResponse: (response: { meta: IGetTokenSuccessResponse }) => {
        return response?.meta;
      },
      invalidatesTags: ["Client"],
    }),

    client: builder.query<IAuthClient, void>({
      query: () => ({
        url: "/api/v1/clients",
      }),
      providesTags: ["Client"],
    }),
    clientInfo: builder.query<IClientInfo, { skipInitialLoad?: boolean }>({
      query: () => ({
        url: "/api/v1/client/info",
      }),
      providesTags: ["Client"],
    }),

    updateProfile: builder.mutation<IAuthClient, IClientPayload>({
      query: (payload) => ({
        url: "/api/v1/client",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (result) => {
        if (result) {
          return ["Client"];
        }
        return [];
      },
    }),

    updatePassword: builder.mutation<IUpdatePassword, IUpdatePasswordPayload>({
      query: (payload) => ({
        url: "/api/v1/web/change-password",
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (result) => {
        if (result) {
          return ["Client"];
        }
        return [];
      },
    }),

    getChangePasswordQuestion: builder.mutation<IGetChangePasswordQuestion, IGetChangePasswordQuestionPayload>({
      query: (payload) => ({
        url: "/api/v1/web/forgot-password/question",
        method: "POST",
        body: {
          phone: formatPhone(payload.phone),
        },
      }),
      invalidatesTags: (result) => {
        if (result) {
          return ["Client"];
        }
        return [];
      },
    }),

    changePassword: builder.mutation<IChangePasswordSuccessResponse, IChangePasswordPayload>({
      query: (payload) => ({
        url: "/api/v1/web/forgot-password/change",
        method: "PATCH",
        body: {
          phone: formatPhone(payload.phone),
          web_password: payload.web_password,
          client_secret: payload.client_secret,
          answer: payload.answer,
        },
      }),
      invalidatesTags: (result) => {
        if (result) {
          return ["Client"];
        }
        return [];
      },
    }),

    deleteClient: builder.mutation<void, void>({
      query: (payload) => ({
        url: "/api/v1/client",
        method: "DELETE",
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetTokenMutation,
  usePhoneCheckMutation,
  useVerifySmsMutation,
  useRegisterConfirmMutation,
  useRegisterMutation,
  useSendVerifySmsMutation,
  useUpdatePasswordMutation,
  useUpdateProfileMutation,
  useClientQuery,
  useClientInfoQuery,
  useChangePasswordMutation,
  useGetChangePasswordQuestionMutation,
  useDeleteClientMutation
} = authApi;

export default authApi.reducer;
